import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, RefreshCw } from 'lucide-react';
import {
  flipkartOauthApi,
  type FlipkartOauthConnection,
} from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';

export const FLIPKART_OAUTH_CONNECTIONS_QUERY_KEY = ['flipkart-oauth-connections'] as const;

async function startFlipkartOAuth(credentialId?: string | null) {
  const popup = window.open('about:blank', '_blank');
  try {
    const res = credentialId
      ? await flipkartOauthApi.reconnect(credentialId)
      : await flipkartOauthApi.getAuthorizeUrl();
    if (!res.success || !res.data?.authorize_url) {
      popup?.close();
      throw new Error('Could not start Flipkart authorization');
    }
    const url = res.data.authorize_url;
    if (popup && !popup.closed) {
      popup.location.href = url;
    } else {
      window.location.href = url;
    }
  } catch (err) {
    popup?.close();
    throw err;
  }
}

export function useFlipkartOauthConnections(enabled = true) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const refetchConnections = () => {
      void queryClient.invalidateQueries({ queryKey: FLIPKART_OAUTH_CONNECTIONS_QUERY_KEY });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refetchConnections();
    };

    window.addEventListener('focus', refetchConnections);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refetchConnections);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, queryClient]);

  return useQuery({
    queryKey: FLIPKART_OAUTH_CONNECTIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await flipkartOauthApi.listConnections();
      if (!res.success) throw new Error('Failed to load Flipkart connections');
      return res.data;
    },
    enabled,
    refetchInterval: 5 * 60_000,
  });
}

export function useConnectFlipkartMutation() {
  return useMutation({
    mutationFn: () => startFlipkartOAuth(),
  });
}

export function useReconnectFlipkartMutation() {
  return useMutation({
    mutationFn: (credentialId: string) => startFlipkartOAuth(credentialId),
  });
}

function healthLabel(health: string): string {
  switch (health) {
    case 'reauth_required':
      return 'Re-authorization required';
    case 'expiring_soon':
      return 'Expiring soon';
    case 'inactive_risk':
      return 'At risk (inactive)';
    default:
      return 'Active';
  }
}

export function FlipkartConnectionsPanel() {
  const { data, isLoading, isError, refetch } = useFlipkartOauthConnections();
  const connect = useConnectFlipkartMutation();
  const reconnect = useReconnectFlipkartMutation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const verify = useMutation({
    mutationFn: async (credentialId: string) => {
      const res = await flipkartOauthApi.verify(credentialId);
      if (!res.success) throw new Error('Verify failed');
      return res.data;
    },
    onSuccess: () => {
      showToast('Flipkart API verified successfully', 'success');
      void queryClient.invalidateQueries({ queryKey: FLIPKART_OAUTH_CONNECTIONS_QUERY_KEY });
    },
    onError: (err: Error) => {
      showToast(err.message || 'Flipkart verify failed', 'error');
    },
  });

  return (
    <div className="space-y-4 overflow-hidden rounded-2xl border border-border/80 bg-white px-4 py-4 shadow-sm sm:px-6 sm:py-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-brand-ink">Flipkart seller</h2>
          <p className="mt-0.5 max-w-md text-sm text-foreground-muted">
            Connect Flipkart seller accounts via Partner OAuth. Access tokens last ~60 days; refresh
            tokens ~180 days — reconnect before they lapse.
          </p>
        </div>
        <Button type="button" className="h-10 rounded-lg bg-brand-ink px-4 hover:bg-chrome-border" onClick={() => connect.mutate()} disabled={connect.isPending}>
          {connect.isPending ? (
            <>
              <Spinner className="h-3.5 w-3.5 mr-1.5" />
              Redirecting…
            </>
          ) : (
            <>
              <Link2 className="h-3.5 w-3.5 mr-1.5" aria-hidden />
              Connect Flipkart
            </>
          )}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-error/20 bg-error-subtle p-4 text-sm text-error-ink">
          Could not load connections.{' '}
          <button type="button" className="underline font-medium" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      ) : (data?.connections?.length ?? 0) === 0 ? (
        <div className="rounded-xl border border-border bg-surface p-6 text-center text-foreground-muted text-sm">
          No Flipkart sellers connected yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {(data?.connections ?? []).map((c: FlipkartOauthConnection) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-border bg-surface px-4 py-3"
            >
              <div className="min-w-0">
                <p className="font-medium text-foreground truncate">
                  {c.display_name || c.seller_id || 'Flipkart connection'}
                </p>
                <p className="text-xs text-foreground-subtle mt-0.5">
                  {healthLabel(c.health)}
                  {c.days_until_expiry != null
                    ? ` · ${c.days_until_expiry} day${c.days_until_expiry === 1 ? '' : 's'} left`
                    : ''}
                  {c.created_by_email ? ` · ${c.created_by_email}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={verify.isPending}
                  onClick={() => verify.mutate(c.id)}
                >
                  {verify.isPending ? (
                    <Spinner className="h-3.5 w-3.5" />
                  ) : (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                      Verify
                    </>
                  )}
                </Button>
                {(c.health === 'reauth_required' ||
                  c.health === 'expiring_soon' ||
                  c.health === 'inactive_risk') && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={reconnect.isPending}
                    onClick={() => reconnect.mutate(c.id)}
                  >
                    Reconnect
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ConnectFlipkartButton({ className }: { className?: string }) {
  const connect = useConnectFlipkartMutation();
  return (
    <Button
      type="button"
      className={className}
      disabled={connect.isPending}
      onClick={() => connect.mutate()}
    >
      {connect.isPending ? (
        <>
          <Spinner className="h-3.5 w-3.5 mr-1.5" />
          Redirecting…
        </>
      ) : (
        <>
          <Link2 className="h-3.5 w-3.5 mr-1.5" aria-hidden />
          Connect Flipkart
        </>
      )}
    </Button>
  );
}
